"use client";

import { ark } from "@ark-ui/react/factory";
import { focusRing } from "@decibeltrade/web-core/recipes";
import { ComponentPropsWithoutRef, forwardRef, ReactNode } from "react";
import { css, cx } from "styled-system/css";

export interface ClickableProps extends ComponentPropsWithoutRef<typeof ark.button> {
  /** A ref to the underlying `button` element */
  ref?: React.RefObject<HTMLButtonElement>;
  /** The content to be rendered inside the clickable element */
  children: ReactNode;
  /** Whether to render as the child element instead of as a button */
  asChild?: boolean;
  /** Class name for applying additional styles */
  className?: string;
  /**
   * An aria-label to describe the clickable action.
   * Useful for accessibility when children is not descriptive text
   */
  ariaLabel?: string;
}

/** A simple wrapper that makes any react element clickable with proper accessibility attributes. */
export const Clickable = forwardRef<HTMLButtonElement, ClickableProps>(
  ({ children, asChild, className, ariaLabel, type = "button", ...props }, ref) => {
    return (
      <ark.button
        ref={ref}
        asChild={asChild}
        className={cx(clickableStyles, focusRing({ rounded: "0" }), className)}
        aria-label={ariaLabel}
        type={type}
        {...props}
      >
        {children}
      </ark.button>
    );
  },
);
Clickable.displayName = "Clickable";

const clickableStyles = css({
  cursor: { base: "pointer", _disabled: "not-allowed" },
  bg: "transparent",
});
